import type { MaybeObservableValue, ObservableValue } from '@amazon/vinyl-observable'
import { asData, data } from '@amazon/vinyl-observable'
import type { LogTarget } from '@amazon/vinyl-util'
import { logError, logInfo } from '@amazon/vinyl-util'
import { formatMessage } from './format.js'
import type { MessageValue } from './format.js'
import { findBestLanguageMatch } from './locale.js'
import { getLocale, loadBundle, resolveLocale } from './store.js'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

type ObservableParams<T> = {
    [K in keyof T]: MaybeObservableValue<T[K]>
}

/**
 * A typed i18n bundle.
 *
 * - `K` enumerates the valid string keys.
 * - `P` maps each key to its required params object (plain values), or
 *   `undefined` if the key has no placeholders. At the call site, each
 *   param value is widened to `MaybeObservableValue` so callers can pass
 *   either static values or observables.
 */
export interface I18nBundle<
    K extends string,
    P extends Record<K, Record<string, string | number> | undefined> = Record<K, undefined>,
> {
    getString<Key extends K>(
        this: void,
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: ObservableParams<P[Key]>]
    ): ObservableValue<string>
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const target: LogTarget = { logPrefix: 'i18n' }

/**
 * Creates a lazily-loaded i18n bundle bound to the given `bundleName`.
 *
 * On the first `.get()` call the bundle JSON for the current locale is
 * fetched. When the global locale changes, the new locale's JSON is fetched
 * and every observable returned by `.get()` is updated automatically.
 */
export function i18nBundle<
    K extends string,
    P extends Record<K, Record<string, string | number> | undefined> = Record<K, undefined>,
>(bundleName: string, availableLocales: readonly string[]): I18nBundle<K, P> {
    const strings = data<Record<string, MessageValue> | null>(null)

    let initialized = false

    async function loadForLocale(locale: string) {
        const resolved = resolveLocale(locale)
        const bestLocale = findBestLanguageMatch(resolved, availableLocales) ?? resolved
        try {
            strings.value = await loadBundle(bundleName, bestLocale)
            logInfo(target, `Loaded bundle "${bundleName}" ${bestLocale}`)
        } catch {
            // leave strings as-is (keys will be returned as fallback)
        }
    }

    function ensureInitialized() {
        if (initialized) return
        initialized = true

        getLocale().onData((locale) => {
            loadForLocale(locale).catch((e) =>
                logError(target, `Failed to load locale "${locale}"`, e)
            )
        })
    }

    function resolveValue(s: Record<string, MessageValue> | null, key: K): MessageValue | null {
        if (!s) return null
        if (!(key in s)) {
            logError(target, `Missing key "${key}"`)
            return key
        }
        return s[key]
    }

    function render(value: MessageValue, params: Record<string, string | number>, key: K): string {
        const resolvedLocale = resolveLocale(getLocale().value)
        try {
            return formatMessage(value, params, resolvedLocale)
        } catch (e) {
            logError(target, `Format error for key "${key}":`, e)
            return typeof value === 'string' ? value : key
        }
    }

    function getString<Key extends K>(
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: P[Key]]
    ): ObservableValue<string> {
        ensureInitialized()

        const params = args[0]

        if (!params || Object.keys(params).length === 0) {
            return strings.map((s) => {
                const value = resolveValue(s, key)
                return value == null ? '' : render(value, {}, key)
            })
        }

        const result = data('')
        const paramEntries = Object.entries(params)
        const observables = paramEntries.map(([, v]) => asData(v))
        const currentValues: (string | number)[] = observables.map((o) => o.value)

        function update() {
            const value = resolveValue(strings.value, key)
            if (value == null) {
                result.value = ''
                return
            }
            const paramObj: Record<string, string | number> = {}
            paramEntries.forEach(([k], i) => {
                paramObj[k] = currentValues[i]
            })
            result.value = render(value, paramObj, key)
        }

        strings.onData(() => update())
        observables.forEach((obs, i) => {
            obs.onData((v) => {
                currentValues[i] = v
                update()
            })
        })

        return result
    }

    return { getString }
}
